import {
  useState,
  useEffect,
  Fragment,
  ChangeEvent,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  useMemo,
} from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  useCreateDataset,
  useCreateRemoteDataset,
  useGetAllDatasetTags,
  useSyncRemoteDataset,
  useGetDatasetChunkingStrategy,
  useGetFilteredDatasetTypes,
} from "src/views/datasets/services/DatasetServices";
import { useGetLicensedProducts } from "src/views/managementConsole/services/LicenseService";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  FormControlLabel,
  FormControl,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TagSelect, { Tag } from "src/reusable/components/select/tag-select";
import { useQueryClient } from "react-query";
import { LoadingButton } from "@mui/lab";
import { isAxiosError } from "axios";
import { extractErrorDetail } from "src/helpers/apiError";
import { ModalBase } from "./ModelBase";
import { ChunkingStrategy, DatasetType } from "src/views/datasets/types";

type Form = {
  name: string;
  description: string;
  tags: Array<Tag>;
  url: string;
  credentials: string;
  dataType: number | null;
  customChunkingStrategy: number | null;
  visibility: "public" | "private";
};

/** A titled group of form controls with an optional helper line. */
const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {description && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        {description}
      </Typography>
    )}
    <Box
      sx={{
        display: "flex",
        flexFlow: "column",
        gap: 2,
        mt: description ? 0 : 1.5,
      }}
    >
      {children}
    </Box>
  </Box>
);

export const CreateDatasetModal = (props: {
  isVisible: boolean;
  onClose: () => void;
  /** When provided, called with the new dataset instead of navigating to its
   * viewer — lets callers (e.g. dataset scoring) select it in place. */
  onCreated?: (dataset: { id: number; name: string }) => void;
}) => {
  const queryClient = useQueryClient();

  // Mutations
  const {
    mutate: createDataset,
    isLoading: isCreateDatasetLoading,
    isSuccess: isCreateDatasetSuccess,
    error: createDataError,
    data: newDataset,
  } = useCreateDataset();

  const {
    mutate: createRemoteDataset,
    isLoading: isCreateRemoteDatasetLoading,
    error: createRemoteDataError,
    isSuccess: isCreateRemoteDatasetSuccess,
    data: newRemoteDataset,
  } = useCreateRemoteDataset();

  const { mutate: syncRemoteDataset, isSuccess: isSyncSuccessful } =
    useSyncRemoteDataset();

  // Queries
  const availableTagsQuery = useGetAllDatasetTags();
  const availableTags = availableTagsQuery.data as Array<Tag> | undefined;

  const { data: datasetChunkingStrategiesData } =
    useGetDatasetChunkingStrategy();
  const datasetChunkingStrategies = datasetChunkingStrategiesData as
    | Array<ChunkingStrategy>
    | undefined;

  const { data: licensedProducts, isError: isLicenseError } =
    useGetLicensedProducts();
  const { data: datasetTypesData } = useGetFilteredDatasetTypes(
    licensedProducts,
    isLicenseError,
  );
  const datasetTypes = datasetTypesData as Array<DatasetType> | undefined;

  // Hooks
  const history = useHistory();
  const { folder_id } = useParams<{ folder_id?: string }>();

  const [toggleRemoteDataset, setToggleRemoteDataset] =
    useState<boolean>(false);

  const [toggleCustomChunking, setToggleCustomChunking] =
    useState<boolean>(false);

  const [form, setForm] = useState<Form>({
    name: "",
    description: "",
    tags: [],
    url: "",
    credentials: "",
    dataType: null,
    customChunkingStrategy: null,
    visibility: "public",
  });

  const showChunkingStrategy: boolean = useMemo(() => {
    const documentType = datasetTypes?.find(
      (type) => type.name?.toLowerCase() === "document",
    );
    return !!documentType && documentType.id === form.dataType;
  }, [datasetTypes, form.dataType]);

  const documentDetails: DatasetType | undefined = useMemo(() => {
    return datasetTypes?.find((type) => type.id === form.dataType);
  }, [datasetTypes, form.dataType]);

  const chunkingDetails: ChunkingStrategy | undefined = useMemo(() => {
    return datasetChunkingStrategies?.find(
      (strat) => strat.id === form.customChunkingStrategy,
    );
  }, [datasetChunkingStrategies, form.customChunkingStrategy]);

  // Handlers

  // Handles optimistic update on useQuery -> availableTags
  // Allows query to be used like UsedState without the need for an effect
  const updateAvailableTags = (tags: Array<Tag>) => {
    queryClient.setQueryData(["allDatasetTags"], () => tags);
  };

  const onChangeHandler = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [e.target.name]: value }));
  };

  const trimmedDatasetName = useMemo(() => form.name.trim(), [form.name]);

  const hasEdgeNameSpaces = useMemo(
    () => form.name !== trimmedDatasetName,
    [form.name, trimmedDatasetName],
  );

  const submitHandler = () => {
    if (trimmedDatasetName.length === 0 || hasEdgeNameSpaces) {
      return;
    }

    if (toggleRemoteDataset === true) {
      createRemoteDataset({
        name: trimmedDatasetName,
        description: form.description,
        dataset_type_id: form.dataType,
        visibility_level: form.visibility,
        chunking_strategy_id: form.customChunkingStrategy,
        remote_dataset: true,
        dataset_folder_fk: folder_id,
        remote_dataset_details: {
          url: form.url,
          credentials: form.credentials,
        },
      });
    } else {
      createDataset({
        name: trimmedDatasetName,
        description: form.description,
        tags: form.tags,
        dataset_type_id: form.dataType,
        visibility_level: form.visibility,
        chunking_strategy_id: form.customChunkingStrategy,
        remote_dataset: false,
        dataset_folder_fk: folder_id,
        remote_dataset_details: {
          url: null,
          credentials: null,
        },
      });
    }
  };

  const onClose = () => {
    setForm({
      name: "",
      description: "",
      tags: [],
      url: "",
      credentials: "",
      dataType: null,
      customChunkingStrategy: null,
      visibility: "public",
    });
    props.onClose();
  };

  // Effects
  useEffect(() => {
    if (isCreateRemoteDatasetSuccess && newRemoteDataset) {
      syncRemoteDataset(newRemoteDataset.id);
    }
  }, [isCreateRemoteDatasetSuccess, newRemoteDataset, syncRemoteDataset]);

  useEffect(() => {
    if (
      isCreateDatasetSuccess ||
      (isCreateRemoteDatasetSuccess && isSyncSuccessful)
    ) {
      const created = newDataset ?? newRemoteDataset;
      onClose();
      // A caller that wants the dataset selected in place (not navigated to)
      // supplies onCreated; otherwise fall back to opening its viewer.
      if (props.onCreated) {
        if (created) props.onCreated({ id: created.id, name: created.name });
      } else if (folder_id) {
        history.push(
          `/datasets/dataset-folders/${folder_id}/dataset/${created?.id}/viewer`,
        );
      } else {
        history.push(`/datasets/${created?.id}/viewer`);
      }
    }
  }, [
    isCreateDatasetSuccess,
    newDataset,
    isCreateRemoteDatasetSuccess,
    isSyncSuccessful,
  ]);

  const isFormValid: boolean = useMemo(() => {
    if (hasEdgeNameSpaces) {
      return false;
    }

    if (trimmedDatasetName.length > 2) {
      if (form.dataType != null && toggleRemoteDataset === false) {
        return true;
      }
      // Remote validation
      if (toggleRemoteDataset === true) {
        const regex =
          /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
        const validUrl = regex.test(form.url);

        if (validUrl && form.credentials.length > 3) {
          if (
            toggleCustomChunking === true &&
            form.customChunkingStrategy === null
          ) {
            return false;
          }
          return true;
        }
      }
    }
    return false;
  }, [
    form,
    toggleCustomChunking,
    toggleRemoteDataset,
    hasEdgeNameSpaces,
    trimmedDatasetName,
  ]);

  const isNameTakenError = useMemo(
    () =>
      (isAxiosError(createDataError) && createDataError.status === 409) ||
      (isAxiosError(createRemoteDataError) &&
        createRemoteDataError.status === 409),
    [createDataError, createRemoteDataError],
  );

  return (
    <ModalBase
      title="Create New Dataset"
      onClose={onClose}
      isVisible={props.isVisible}
      dialogTestId="ds-modal-create"
    >
      <FormControl
        sx={{
          display: "flex",
          flexFlow: "column",
          gap: 3,
          marginTop: "12px",
        }}
      >
        {(createDataError !== null || createRemoteDataError !== null) &&
          !isNameTakenError && (
            <Alert severity="error">
              {extractErrorDetail(
                createDataError ?? createRemoteDataError,
                "Something went wrong!",
              )}
            </Alert>
          )}

        <Section title="Details">
          <TextField
            required
            label="Name"
            name="name"
            id="outlined-basic"
            inputProps={{ "data-testid": "ds-modal-create-input-name" }}
            variant="outlined"
            fullWidth
            placeholder="Enter name"
            onChange={onChangeHandler}
            value={form.name}
            error={isNameTakenError || hasEdgeNameSpaces}
            helperText={
              isNameTakenError
                ? "Name is already in use"
                : hasEdgeNameSpaces
                  ? "Name cannot start or end with a space"
                  : ""
            }
          />
          <TextField
            multiline
            minRows={3}
            maxRows={3}
            label="Description"
            name="description"
            id="description-input"
            inputProps={{ "data-testid": "ds-modal-create-input-desc" }}
            variant="outlined"
            fullWidth
            placeholder="Enter a description if needed"
            onChange={onChangeHandler}
            value={form.description}
          />
          <TagSelect
            value={form.tags}
            onChange={(tags) => {
              setForm((prev) => ({ ...prev, tags }));
            }}
            availableTags={availableTags ?? []}
            setAvailableTags={(tags) => {
              setForm((prev) => ({
                ...prev,
                tags: [...prev.tags, tags[0]],
              }));
              updateAvailableTags(tags);
            }}
            placeholder="Add tags"
            allowCreation={true}
            testId="ds-modal-create-input-tags"
          />
        </Section>

        <Divider />

        <Section
          title="Access"
          description="Choose who can see this dataset. You can share it with specific people or groups after creating."
        >
          <RadioGroup
            value={form.visibility}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                visibility: e.target.value as "public" | "private",
              }))
            }
          >
            <FormControlLabel
              value="public"
              control={
                <Radio
                  size="small"
                  // Cast: MUI types inputProps as InputHTMLAttributes, which
                  // has no index signature for data-* keys.
                  inputProps={
                    {
                      "data-testid": "ds-modal-create-radio-public",
                    } as InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Anyone in the organization
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Everyone can view this dataset
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="private"
              control={
                <Radio
                  size="small"
                  inputProps={
                    {
                      "data-testid": "ds-modal-create-radio-private",
                    } as InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Only specific people &amp; groups
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only you (and those you share it with) can view it. You can
                    share it after creating.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Section>

        <Divider />

        <Section
          title="Type"
          description="What kind of data this dataset holds — this sets how it is stored and processed."
        >
          {toggleRemoteDataset ? (
            <Alert severity="info">
              This dataset loads from a remote URL, configured under
              &quot;Advanced&quot; below.
            </Alert>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Dataset type</InputLabel>
              <Select
                required
                name="dataType"
                label="Dataset type"
                // On the SelectDisplay, not the Select root: a plain
                // data-testid here lands on the wrapper div, which does not
                // carry the open handler (see TESTID_INJECTION_SPEC.md).
                SelectDisplayProps={
                  {
                    "data-testid": "ds-modal-create-select-type",
                  } as HTMLAttributes<HTMLDivElement>
                }
                value={form.dataType != null ? String(form.dataType) : ""}
                renderValue={(val) => (
                  <span style={{ textTransform: "capitalize" }}>
                    {datasetTypes?.find((t) => String(t.id) === val)?.name ??
                      ""}
                  </span>
                )}
                onChange={(e: SelectChangeEvent) => {
                  const isDocument = !!datasetTypes?.find(
                    (v) =>
                      v?.name?.toLowerCase() === "document" &&
                      v.id === parseInt(e.target.value),
                  );
                  setForm((prev) => ({
                    ...prev,
                    dataType: isNaN(parseInt(e.target.value))
                      ? null
                      : parseInt(e.target.value),
                    customChunkingStrategy: isDocument ? -7 : null,
                  }));
                }}
              >
                {datasetTypes?.map((item) => {
                  if (item.name !== "AtomResults")
                    return (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                        data-testid={`ds-modal-create-select-type-option-${item.id}`}
                      >
                        <Stack sx={{ py: 0.25 }}>
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {item.name}
                          </Typography>
                          {item.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: "normal" }}
                            >
                              {item.description}
                            </Typography>
                          )}
                        </Stack>
                      </MenuItem>
                    );
                })}
              </Select>
              {documentDetails?.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: "8px" }}
                >
                  {documentDetails.description}
                </Typography>
              )}
            </FormControl>
          )}
          {(showChunkingStrategy || toggleRemoteDataset) && (
            <Fragment>
              <FormControlLabel
                sx={{ mb: 0 }}
                control={
                  <Switch
                    checked={toggleCustomChunking}
                    onChange={() => {
                      if (
                        toggleCustomChunking &&
                        form.customChunkingStrategy !== -7
                      ) {
                        setForm((prev) => ({
                          ...prev,
                          customChunkingStrategy: null,
                        }));
                      }
                      setToggleCustomChunking((prev) => !prev);
                    }}
                  />
                }
                label="Custom chunking"
              />
              <Typography variant="caption" sx={{ opacity: 0.7, mt: "-12px" }}>
                Select how the data will be divided before being sent to the
                model.
              </Typography>
              {toggleCustomChunking && (
                <FormControl fullWidth>
                  <InputLabel>Chunking Strategy</InputLabel>
                  <Select
                    required
                    name="customChunkingStrategy"
                    label="Chunking Strategy"
                    value={form.customChunkingStrategy?.toString() ?? ""}
                    onChange={(e: SelectChangeEvent) =>
                      setForm((prev) => ({
                        ...prev,
                        customChunkingStrategy: isNaN(parseInt(e.target.value))
                          ? null
                          : parseInt(e.target.value),
                      }))
                    }
                  >
                    {datasetChunkingStrategies?.map((item) => (
                      <MenuItem
                        key={item.id}
                        sx={{ textTransform: "capitalize" }}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {chunkingDetails && (
                    <Typography variant="caption" sx={{ my: "8px" }}>
                      {chunkingDetails.comment}
                    </Typography>
                  )}
                </FormControl>
              )}
            </Fragment>
          )}
        </Section>

        <Divider />

        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: "transparent",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0, minHeight: "auto" }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Advanced
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Remote data source and other rarely-needed options.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{ px: 0, display: "flex", flexFlow: "column", gap: 2 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                sx={{ marginBottom: 0 }}
                control={
                  <Switch
                    checked={toggleRemoteDataset}
                    onChange={() => {
                      setToggleRemoteDataset((prev) => !prev);
                      setForm((prev) => ({ ...prev, dataType: null }));
                    }}
                  />
                }
                label="Load from a remote URL"
              />
              <Chip
                size="small"
                sx={{ width: "fit-content" }}
                label="Preview"
              />
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.7, mt: "-12px" }}>
              Retrieve your data via a URL. Useful for large datasets or when
              the file is not on your machine. Replaces the dataset type above.
            </Typography>
            {toggleRemoteDataset && (
              <Fragment>
                <TextField
                  label="URL"
                  name="url"
                  id="remoteData-url-input"
                  variant="outlined"
                  fullWidth
                  placeholder="Please enter a valid URL"
                  onChange={onChangeHandler}
                  value={form.url}
                />
                <TextField
                  multiline
                  minRows={3}
                  maxRows={3}
                  label="Credentials"
                  name="credentials"
                  id="remoteData-credentials-input"
                  variant="outlined"
                  fullWidth
                  placeholder="Please provide a token if needed"
                  onChange={onChangeHandler}
                  value={form.credentials}
                />
              </Fragment>
            )}
          </AccordionDetails>
        </Accordion>

        <Divider />

        <LoadingButton
          onClick={submitHandler}
          sx={{ width: "fit-content", ml: "auto" }}
          variant="contained"
          disabled={!isFormValid}
          loading={isCreateDatasetLoading || isCreateRemoteDatasetLoading}
          data-testid="ds-modal-create-btn-submit"
        >
          Create New Dataset
        </LoadingButton>
      </FormControl>
    </ModalBase>
  );
};
